import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { TabbedPage, TabbedPageHeader, TabConfig } from "@/components/tabbed-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  BoxesIcon,
  ShoppingCart,
  Truck,
  Loader2,
  Package,
} from "lucide-react";

// ==================== STOCK OVERVIEW TAB ====================
interface InventoryItem {
  id: string;
  productName: string;
  totalCards: number;
  availableCards: number;
  issuedCards: number;
  createdAt: string;
}

function StockOverviewTab() {
  const { data: inventory, isLoading } = useQuery<InventoryItem[]>({
    queryKey: ["inventory"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/inventory");
      if (!response.ok) throw new Error("Failed to fetch inventory");
      return response.json();
    },
  });

  const totalCards = inventory?.reduce((sum, item) => sum + item.totalCards, 0) ?? 0;
  const availableCards = inventory?.reduce((sum, item) => sum + item.availableCards, 0) ?? 0;
  const issuedCards = inventory?.reduce((sum, item) => sum + item.issuedCards, 0) ?? 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Cards</p>
                <p className="text-2xl font-bold">{totalCards.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-primary/10">
                <Package className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Available</p>
                <p className="text-2xl font-bold text-success">{availableCards.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-success/10">
                <BoxesIcon className="h-5 w-5 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Issued</p>
                <p className="text-2xl font-bold text-amber-500">{issuedCards.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <ShoppingCart className="h-5 w-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory by Product</CardTitle>
          <CardDescription>{inventory?.length ?? 0} product(s) in inventory</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : inventory?.length === 0 ? (
            <div className="text-center py-12">
              <BoxesIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No inventory</h3>
              <p className="text-muted-foreground">Purchase cards to add to inventory.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Issued</TableHead>
                  <TableHead className="text-right">Usage</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventory?.map((item) => {
                  const usagePercent = item.totalCards > 0 ? Math.round((item.issuedCards / item.totalCards) * 100) : 0;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.productName}</TableCell>
                      <TableCell className="text-right font-mono">{item.totalCards.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono text-success">{item.availableCards.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{item.issuedCards.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={usagePercent > 80 ? "destructive" : usagePercent > 50 ? "warning" : "success"}>
                          {usagePercent}%
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PURCHASE ORDERS TAB ====================
interface Product {
  id: string;
  name: string;
}

function PurchaseOrdersTab() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    productId: "",
    totalCards: 100,
    availableCards: 100,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/products");
      if (!response.ok) throw new Error("Failed to fetch products");
      return response.json();
    },
  });

  const buyMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/inventory/buy", data);
      if (!response.ok) throw new Error("Failed to buy cards");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      toast({ title: "Success", description: "Cards purchased successfully" });
      setFormData({ productId: "", totalCards: 100, availableCards: 100 });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    buyMutation.mutate(formData);
  };

  return (
    <div className="space-y-6">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Purchase Cards
          </CardTitle>
          <CardDescription>Order new card inventory for your program</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={formData.productId} onValueChange={(v) => setFormData({ ...formData, productId: v })}>
                <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {products?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={formData.totalCards}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setFormData({ ...formData, totalCards: val, availableCards: val });
                }}
                min={1}
                max={10000}
              />
            </div>

            <Button type="submit" disabled={buyMutation.isPending || !formData.productId}>
              {buyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Purchase Cards
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SHIPPING TAB ====================
interface ShippingRecord {
  id: string;
  quantity: number;
  shippingAddress: string | null;
  trackingNumber: string | null;
  carrier: string | null;
  status: string;
  shippedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

function ShippingTab() {
  const { data: records, isLoading } = useQuery<ShippingRecord[]>({
    queryKey: ["shipping-records"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/inventory/shipping");
      if (!response.ok) throw new Error("Failed to fetch shipping records");
      return response.json();
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "success" | "secondary" | "outline"> = {
      DELIVERED: "success",
      SHIPPED: "secondary",
      PENDING: "outline",
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shipment History</CardTitle>
        <CardDescription>{records?.length ?? 0} shipment(s)</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : records?.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No shipments</h3>
            <p className="text-muted-foreground">No card shipments yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quantity</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Shipped</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records?.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.quantity}</TableCell>
                  <TableCell className="max-w-xs truncate">{record.shippingAddress || "-"}</TableCell>
                  <TableCell>{record.carrier || "-"}</TableCell>
                  <TableCell className="font-mono text-sm">{record.trackingNumber || "-"}</TableCell>
                  <TableCell>{getStatusBadge(record.status)}</TableCell>
                  <TableCell>{record.shippedAt ? new Date(record.shippedAt).toLocaleDateString() : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== MAIN INVENTORY PAGE ====================
export default function Inventory() {
  const tabs: TabConfig[] = [
    {
      id: "stock",
      label: "Stock Overview",
      icon: BoxesIcon,
      content: <StockOverviewTab />,
    },
    {
      id: "orders",
      label: "Purchase Orders",
      icon: ShoppingCart,
      content: <PurchaseOrdersTab />,
    },
    {
      id: "shipping",
      label: "Shipping",
      icon: Truck,
      content: <ShippingTab />,
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <TabbedPageHeader
        title="Inventory"
        description="Manage card stock, purchases, and shipments"
      />
      <TabbedPage tabs={tabs} defaultTab="stock" />
    </div>
  );
}
